import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type Cell, type Row, flexRender } from "@tanstack/react-table";
import React, { type CSSProperties, Fragment, useMemo } from "react";

import CustomRightClickActionBtn from "../buttons/WpxRightClickActionButton";

import { getCommonPinningStyles } from "./ColumnPinning";

import {
  ACTION_ID,
  ROW_DND_DRAG_HANDLE_ID,
  SELECTOR_ID,
  SELECTOR_ID_COL_SIZE,
} from "@/app/constants/global-constants";
import * as Table from "@/components/ui/table";
import type { GlobalRowDataTypes } from "@/interfaces/globalTable.interface";
import { cn } from "@/utils/core/generalUtils";

interface DragAlongCellProps<T, V> {
  cell: Cell<T, V>;
  highLight: boolean;
  isActionIdLastColumn: boolean;
  isMobile?: boolean;
  rowIdx: number;
  columnDraggable?: boolean;
  rowDraggable?: boolean;
  enableEnhancedUX?: boolean;
  activeColumnId?: string | null;
  rowDragHandleColumnId?: string;
  rowDragHandleListeners?: ReturnType<typeof useSortable>["listeners"];
  rowDragHandleAttributes?: ReturnType<typeof useSortable>["attributes"];
  rowSetActivatorNodeRef?: ReturnType<
    typeof useSortable
  >["setActivatorNodeRef"];
}

/**
 * Renders a single draggable table cell with pinning and selection support.
 *
 * @param {DragAlongCellProps<T, V>} props - Component props.
 * @returns {JSX.Element} Rendered table cell.
 */
const DragAlongCellComponent = <T, V>(
  props: DragAlongCellProps<T, V>,
): JSX.Element => {
  const {
    cell,
    highLight,
    isActionIdLastColumn,
    isMobile,
    rowIdx,
    columnDraggable,
    rowDraggable,
    enableEnhancedUX,
    activeColumnId,
    rowDragHandleColumnId,
    rowDragHandleListeners,
    rowDragHandleAttributes,
    rowSetActivatorNodeRef,
  } = props;

  const isSelectId = cell.column.columnDef.id === SELECTOR_ID;
  const isActionId = cell.column.columnDef.id === ACTION_ID;
  const isRowDragHandleId = cell.column.columnDef.id === ROW_DND_DRAG_HANDLE_ID;
  const isColumnOrderable =
    cell.column.columnDef.meta?.isColumnOrderable ?? true;
  const isRowDragHandleColumn = cell.column.id === rowDragHandleColumnId;
  const isPinned = cell.column.getIsPinned();
  const isColumnDragDisabled =
    !columnDraggable ||
    isSelectId ||
    isActionId ||
    Boolean(isPinned) ||
    !isColumnOrderable ||
    Boolean(rowDraggable && isRowDragHandleColumn);

  const {
    isDragging: isColumnDragging,
    setNodeRef: setColumnNodeRef,
    transform: columnTransform,
    transition: columnTransition,
  } = useSortable({
    id: cell.column.id,
    disabled: isColumnDragDisabled,
  });

  const isActiveColumn = Boolean(
    columnDraggable && activeColumnId && activeColumnId === cell.column.id,
  );

  const style: CSSProperties = {
    opacity: isColumnDragging && !isPinned ? 0.4 : undefined,
    position: isColumnDragging ? "relative" : undefined,
    transform: CSS.Translate.toString(columnTransform),
    transition: columnTransition,
    zIndex: isColumnDragging || isActiveColumn ? 10 : 0,
  };

  const pinningStyles = useMemo(
    () =>
      getCommonPinningStyles({
        column: cell.column,
        isRow: true,
        disablePinning: isMobile,
      }),
    [cell.column, isMobile],
  );

  let content: React.ReactNode = flexRender(
    cell.column.columnDef.cell,
    cell.getContext(),
  );

  if (isSelectId) {
    content = (
      <div
        className={cn(
          "invisible group-hover/tableRow:visible min-h-8 flex items-center",
          highLight && "visible",
        )}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    );
  } else if (cell.column.id === rowDragHandleColumnId) {
    content = (
      <div
        className='max-w-[10px] opacity-0 group-hover/tableRow:opacity-100 transition-opacity'
        ref={rowSetActivatorNodeRef}
        {...rowDragHandleListeners}
        {...rowDragHandleAttributes}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    );
  }

  const isAlternateRow = typeof rowIdx === "number" && rowIdx % 2 !== 0;

  return (
    <Table.TableCell
      className={cn(
        "text-black/70 dark:text-white/70 border-r last:border-r-0 transition-colors",
        (isSelectId || isRowDragHandleId) && "border-r-0",
        isActionIdLastColumn && "[&:nth-last-child(2)]:border-r-0",
        cell.column?.columnDef?.meta?.classNameRow,
        isPinned && [
          "group-hover/tableRow:bg-secondary group-data-[state=selected]/tableRow:bg-muted",
          enableEnhancedUX || !isAlternateRow
            ? "bg-[var(--bg-table-row)]"
            : "bg-muted",
          enableEnhancedUX && "group-data-[open=true]/tableRow:bg-secondary",
        ],
      )}
      ref={setColumnNodeRef}
      style={{
        ...style,
        ...pinningStyles,
        width: isSelectId ? SELECTOR_ID_COL_SIZE : undefined,
      }}
    >
      {content}
    </Table.TableCell>
  );
};

const DragAlongCell = React.memo(
  DragAlongCellComponent,
) as typeof DragAlongCellComponent;

interface TableRowProps<TData, _TValue = unknown> {
  row: Row<TData>;
  isSelected: boolean;
  onRowClick?: (id: string) => void;
  RightClickData?: (row: GlobalRowDataTypes<TData>) => ActionButtonDataTypes[];
  className?: string;
  columnDraggable?: boolean;
  rowDraggable?: boolean;
  rowDragHandleColumnId?: string;
  getRowDndId?: (row: Row<TData>) => string;
  isRowDragDisabled?: (row: Row<TData>) => boolean;
  isMobile?: boolean;
  rowIdx: number;
  enableEnhancedUX?: boolean;
  activeColumnId?: string | null;
  /** Keeps memoized row cells in sync with updated column renderer closures. */
  cellRenderKey?: unknown;
  columnOrder: string[];
  /** Comma-joined visible column IDs — triggers memo re-render on visibility change. */
  visibleColumnIds: string;
  /** Passed purely to break the React.memo cache when column closures update (e.g. for inline editing) */
  columnsDependency?: unknown[];
}

/**
 * Renders a single table row with drag-and-drop, right-click, and selection support.
 *
 * @param {TableRowProps<T, V>} props - Component props.
 * @returns {React.JSX.Element} Rendered table row.
 */
const TableRowComponent = <T extends { id?: string }, V>(
  props: TableRowProps<T, V>,
): React.JSX.Element => {
  const {
    row,
    isSelected,
    onRowClick,
    RightClickData,
    className,
    columnDraggable = true,
    rowDraggable = false,
    rowDragHandleColumnId,
    getRowDndId,
    isRowDragDisabled,
    isMobile,
    rowIdx,
    enableEnhancedUX,
    activeColumnId,
    columnOrder,
  } = props;

  const RightClickComp = RightClickData ? CustomRightClickActionBtn : Fragment;
  const getRowActions =
    RightClickData ??
    ((_: GlobalRowDataTypes<V>): ActionButtonDataTypes[] => {
      return [];
    });

  const highLight = isSelected;

  const visibleCells = row.getVisibleCells();

  const draggableColumnOrder = React.useMemo(() => {
    const pinnedColumnIds = new Set(
      visibleCells
        .filter((cell) => cell.column.getIsPinned())
        .map((cell) => cell.column.id),
    );
    return columnOrder.filter((id) => !pinnedColumnIds.has(id));
  }, [columnOrder, visibleCells]);

  const lastVisibleColumnId = visibleCells[visibleCells.length - 1]?.column.id;
  const isActionIdLastColumn = lastVisibleColumnId === ACTION_ID;

  const rowDndId =
    getRowDndId?.(row) ?? row.original?.id ?? (row.id as string | undefined);
  const rowDragDisabled =
    !rowDraggable || !rowDndId || isRowDragDisabled?.(row);
  const {
    attributes: rowDragAttributes,
    listeners: rowDragListeners,
    setNodeRef: rowSetNodeRef,
    setActivatorNodeRef,
    transform: rowTransform,
    transition: rowTransition,
    isDragging: isRowDragging,
  } = useSortable({
    id: rowDndId ?? row.id,
    disabled: Boolean(rowDragDisabled),
  });

  return (
    <RightClickComp
      {...(RightClickData && {
        row,
        enableEnhancedUX,
        data: getRowActions,
      })}
      key={row.id}
    >
      <Table.TableRow
        key={row.id}
        data-state={isSelected ? "selected" : undefined}
        className={cn(
          "hover:bg-secondary group group/tableRow",
          enableEnhancedUX &&
            "border-b even:bg-transparent data-[open=true]:bg-secondary hover:[--bg-table-row:hsl(var(--secondary))] data-[open=true]:[--bg-table-row:hsl(var(--secondary))] data-[state=selected]:[--bg-table-row:hsl(var(--muted))]",
          className,
        )}
        ref={rowDraggable ? rowSetNodeRef : undefined}
        style={
          rowDraggable
            ? {
                transform: CSS.Transform.toString(rowTransform),
                transition: rowTransition,
                opacity: isRowDragging ? 0.6 : 1,
              }
            : undefined
        }
        onClick={() =>
          onRowClick && row.original.id && onRowClick(row.original?.id)
        }
      >
        <SortableContext
          items={draggableColumnOrder}
          strategy={horizontalListSortingStrategy}
          disabled={!columnDraggable}
        >
          {visibleCells.map((cell) => {
            return (
              <DragAlongCell
                rowIdx={rowIdx}
                key={cell.id}
                cell={cell}
                highLight={highLight}
                isMobile={isMobile}
                isActionIdLastColumn={isActionIdLastColumn}
                columnDraggable={columnDraggable}
                rowDraggable={rowDraggable}
                enableEnhancedUX={enableEnhancedUX}
                activeColumnId={activeColumnId}
                rowDragHandleColumnId={rowDragHandleColumnId}
                rowDragHandleListeners={
                  rowDraggable && !rowDragDisabled
                    ? rowDragListeners
                    : undefined
                }
                rowDragHandleAttributes={
                  rowDraggable && !rowDragDisabled
                    ? rowDragAttributes
                    : undefined
                }
                rowSetActivatorNodeRef={
                  rowDraggable && !rowDragDisabled
                    ? setActivatorNodeRef
                    : undefined
                }
              />
            );
          })}
        </SortableContext>
      </Table.TableRow>
    </RightClickComp>
  );
};

const TableRow = React.memo(TableRowComponent) as typeof TableRowComponent;

export default TableRow;

interface EmptyTableRowProps {
  hideEmptyRow: boolean;
  getContent: () => React.ReactNode;
}

/**
 * Placeholder row shown when the table has no data.
 *
 * @param {EmptyTableRowProps} props - Component props.
 * @returns {React.JSX.Element} Rendered empty state row.
 */
const EmptyTableRowComponent = (
  props: EmptyTableRowProps,
): React.JSX.Element => {
  const { hideEmptyRow, getContent } = props;

  return (
    <Table.TableRow
      className={cn(
        "hover:bg-white dark:hover:bg-inherit",
        hideEmptyRow && "hidden",
      )}
    >
      <Table.TableCell colSpan={10} className='h-24 text-center'>
        {getContent()}
      </Table.TableCell>
    </Table.TableRow>
  );
};

export const EmptyTableRow = React.memo(
  EmptyTableRowComponent,
) as typeof EmptyTableRowComponent;
